import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import AuthShell from '../components/AuthShell';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  };

  // No token in the URL — the link is broken or was typed by hand
  if (!token) {
    return (
      <AuthShell>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{
            fontFamily: 'var(--tandm-font-display)',
            fontSize: 32, fontWeight: 500, color: 'var(--tandm-on-dark)',
            letterSpacing: '-0.03em', margin: '0 0 6px', lineHeight: 1.1,
          }}>
            Invalid<br />
            <span style={{
              background: 'var(--tandm-gradient-text)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>reset link.</span>
          </h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0, lineHeight: 1.6 }}>
            This link is missing its reset token. Request a new one and use the
            link from the most recent email.
          </p>
        </div>
        <Link to="/forgot-password" className="lf-btn" style={{ textDecoration: 'none' }}>
          Request a new link
        </Link>
        <p style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.3)', marginTop: 24, marginBottom: 0 }}>
          <Link to="/login" className="lf-link">Back to sign in</Link>
        </p>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div style={{ marginBottom: 28 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14, marginBottom: 20,
            background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" fill="none" stroke="#4ADE80" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 style={{
            fontFamily: 'var(--tandm-font-display)',
            fontSize: 32, fontWeight: 500, color: 'var(--tandm-on-dark)',
            letterSpacing: '-0.03em', margin: '0 0 6px', lineHeight: 1.1,
          }}>
            Password<br />
            <span style={{
              background: 'var(--tandm-gradient-text)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>updated.</span>
          </h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0, lineHeight: 1.6 }}>
            Your password has been changed. Sign in with your new password to
            get back to your internships.
          </p>
        </div>
        <Link to="/login" className="lf-btn" style={{ textDecoration: 'none' }}>
          Sign in <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      {/* Headline */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontFamily: 'var(--tandm-font-display)',
          fontSize: 32, fontWeight: 500, color: 'var(--tandm-on-dark)',
          letterSpacing: '-0.03em', margin: '0 0 6px', lineHeight: 1.1,
        }}>
          Choose a new<br />
          <span style={{
            background: 'var(--tandm-gradient-text)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>password.</span>
        </h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
          Make it at least 8 characters
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
          color: '#FCA5A5', borderRadius: 10, padding: '10px 14px',
          marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <label className="lf-label">New password</label>
          <div className="pw-wrap">
            <input
              name="password" type={showPassword ? 'text' : 'password'} required autoFocus
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="lf-input" placeholder="At least 8 characters"
              style={{ paddingRight: 44 }}
            />
            <button type="button" className="pw-btn" onClick={() => setShowPassword(s => !s)}>
              {showPassword
                ? <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                : <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              }
            </button>
          </div>
        </div>

        <div>
          <label className="lf-label">Confirm password</label>
          <input
            name="confirm" type={showPassword ? 'text' : 'password'} required
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="lf-input" placeholder="Repeat your new password"
          />
        </div>

        <button type="submit" disabled={loading} className="lf-btn" style={{ marginTop: 4 }}>
          {loading
            ? <><div className="lf-spinner" />Updating...</>
            : <>Update password <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg></>
          }
        </button>
      </form>

      {/* Footer */}
      <p style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.3)', marginTop: 24, marginBottom: 0 }}>
        <Link to="/login" className="lf-link">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}
